import { useState } from 'react'
import { H2, SmallText } from '../../components/typography/Typography'
import { Button } from '../../components/ui-blocks/Button'
import { supabase } from '../../lib/supabaseClient'
import { ANALYTICS_EVENTS, track } from '../../lib/analytics'

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export const NewsletterSignup = () => {
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle')
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = email.trim()
    if (!EMAIL_REGEX.test(trimmed)) {
      setError('Please enter a valid email address.')
      setStatus('error')
      return
    }
    setError(null)
    setStatus('submitting')

    try {
      const { data, error: invokeError } = await supabase.functions.invoke<{
        ok?: boolean
        error?: string
        alreadySubscribed?: boolean
        welcomeEmailSent?: boolean
      }>('newsletter-subscribe', { body: { email: trimmed, source: 'footer' } })

      if (invokeError || !data?.ok) {
        track(ANALYTICS_EVENTS.NEWSLETTER_SUBSCRIBE_FAILED, {
          source: 'footer',
          reason: invokeError ? 'invoke_error' : 'subscribe_error',
          detail: data?.error || invokeError?.message,
        })
        setError(data?.error || 'Could not subscribe right now. Please try again later.')
        setStatus('error')
        return
      }

      track(ANALYTICS_EVENTS.NEWSLETTER_SUBSCRIBE_SUCCEEDED, {
        source: 'footer',
        alreadySubscribed: !!data.alreadySubscribed,
        welcomeEmailSent: !!data.welcomeEmailSent,
      })
      setStatus('success')
      setEmail('')
    } catch (err) {
      track(ANALYTICS_EVENTS.NEWSLETTER_SUBSCRIBE_FAILED, {
        source: 'footer',
        reason: 'unexpected_error',
        detail: err instanceof Error ? err.message : String(err),
      })
      setError('Could not subscribe right now. Please try again later.')
      setStatus('error')
    }
  }

  return (
    <div className='flex flex-col lg:flex-row justify-between gap-6 lg:items-center'>
      <div className='flex flex-col gap-2 lg:max-w-md'>
        <H2 className='text-white text-2xl md:text-3xl'>Subscribe to our newsletter</H2>
        <SmallText className='text-gray-200 font-light normal-case tracking-normal'>
          Product updates, project highlights, and seating insights — straight to your inbox.
        </SmallText>
      </div>

      {status === 'success' ? (
        <SmallText className='text-my-orange normal-case tracking-normal'>
          Thanks for subscribing! We'll be in touch.
        </SmallText>
      ) : (
        <form
          onSubmit={handleSubmit}
          className='flex flex-col sm:flex-row gap-3 w-full lg:w-1/2'
        >
          <label htmlFor='newsletter-email' className='sr-only'>
            Email address
          </label>
          <input
            id='newsletter-email'
            type='email'
            required
            value={email}
            onChange={(e) => {
              setEmail(e.target.value)
              if (status === 'error') setStatus('idle')
            }}
            placeholder='you@example.com'
            disabled={status === 'submitting'}
            className='flex-1 rounded-full px-4 py-2 md:py-3 bg-white text-gray-900 placeholder-gray-400 font-inter focus:outline-none focus:ring-2 focus:ring-orange-600 disabled:opacity-50'
          />
          <Button
            type='submit'
            variant='primary'
            disabled={status === 'submitting'}
            className='sm:w-auto sm:px-8 whitespace-nowrap'
          >
            {status === 'submitting' ? 'Subscribing…' : 'Subscribe'}
          </Button>
        </form>
      )}

      {status === 'error' && error && (
        <SmallText className='text-red-400 normal-case tracking-normal lg:absolute'>
          {error}
        </SmallText>
      )}
    </div>
  )
}
